import { useState } from "react";
import { T } from "../utils/theme";
import { api, ENDPOINTS, formatDate, cvssToSeverity, CVSS_TABLE } from "../utils/api";
import { useFetch, useToast, useModal } from "../hooks";
import {
  Card, Spinner, EmptyState, SectionHeader, SearchInput, StatusBadge, SeverityBadge,
  CvssScore, Modal, Field, Tabs, ToastStack,
} from "../components/UI";
import { Bug, CheckCircle, XCircle, AlertTriangle, Eye } from "lucide-react";

/*
  CORRECTIONS vs doc API :

  1. ENDPOINTS.reportsByCompany → GET /reports/by-company (Auth + Entreprise)
     Pour l'admin, on utilise le même endpoint avec le token admin.

  2. PUT /reports/status → corps : { reportId, status, cvssScoreFinal, originalReportId? }
     - Les valeurs de status selon la doc : "en_attente" | "validé" | "rejeté" | "duplicate"
     - Pour status=duplicate, originalReportId doit être fourni.

  3. Attribution automatique des points selon le cvssScoreFinal (fait côté serveur).
*/

const ReportDetailModal = ({ report, open, onClose, onUpdateStatus, token, loading }) => {
  const [cvss, setCvss]                         = useState(report?.cvssScore || "");
  const [duplicateOf, setDuplicateOf]           = useState("");

  if (!report) return null;

  const handleUpdate = (status) => {
    const payload = {
      reportId:      report.id,
      status,
      cvssScoreFinal: parseFloat(cvss) || 0,
      // CORRECTION : originalReportId requis si status === "duplicate"
      ...(status === "duplicate" && duplicateOf ? { originalReportId: duplicateOf } : {}),
    };
    onUpdateStatus(payload);
  };

  return (
    <Modal open={open} onClose={onClose} title="Détails du Rapport" width={620}>
      {/* Header */}
      <div style={{
        background: T.bg, borderRadius: 12, padding: "16px 18px",
        marginBottom: 20, border: `1px solid ${T.border}`,
      }}>
        <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
          <SeverityBadge severity={report.severite || cvssToSeverity(report.cvssScore)} />
          <StatusBadge status={report.statut || "en_attente"} />
          <CvssScore score={report.cvssScore || report.cvssScoreFinal} />
        </div>
        <div style={{ fontFamily: T.display, fontWeight: 800, fontSize: 17, marginBottom: 4 }}>{report.titre}</div>
        <div style={{ fontSize: 11, color: T.blue, fontFamily: T.mono }}>ID: {report.id}</div>
      </div>

      {/* Description */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: ".08em", marginBottom: 8 }}>DESCRIPTION</div>
        <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.7, background: T.bg, borderRadius: 10, padding: 14 }}>
          {report.description || "Aucune description fournie."}
        </div>
      </div>

      {/* Markdown / PoC */}
      {report.markdown && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: ".08em", marginBottom: 8 }}>PROOF OF CONCEPT</div>
          <div className="code-block" style={{ maxHeight: 160, whiteSpace: "pre-wrap" }}>{report.markdown}</div>
        </div>
      )}

      {/* Infos grid */}
      <div className="grid-2" style={{ marginBottom: 20 }}>
        {[
          { l: "Hacker",     v: report.hacker?.pseudo   || report.hackerId?.slice(0, 12) },
          { l: "Programme",  v: report.programme?.titre  || report.programId?.slice(0, 12) },
          { l: "Soumis le",  v: formatDate(report.createdAt) },
          { l: "Récompense", v: report.reward ? `${report.reward}€` : "Non attribuée" },
        ].map((f, i) => f.v && (
          <div key={i} style={{ background: T.bg, borderRadius: 8, padding: "10px 14px" }}>
            <div style={{ fontSize: 9, color: T.muted, fontWeight: 700, letterSpacing: ".08em", marginBottom: 3 }}>
              {f.l.toUpperCase()}
            </div>
            <div style={{ fontSize: 12, color: T.text }}>{f.v}</div>
          </div>
        ))}
      </div>

      {/* CVSS Reference table */}
      <div style={{
        background: T.bg, borderRadius: 10, padding: 14, marginBottom: 20,
        border: `1px solid ${T.border}`,
      }}>
        <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: ".08em", marginBottom: 10 }}>
          TABLE CVSS — ATTRIBUTION DES POINTS (automatique côté serveur)
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {CVSS_TABLE.map((row, i) => (
            <div key={i} style={{ background: T.card, borderRadius: 7, padding: "6px 10px", fontSize: 10 }}>
              <span style={{ color: row.color, fontWeight: 700 }}>{row.sev}</span>
              <span style={{ color: T.muted }}> · {row.range} · </span>
              <span style={{ color: T.yellow }}>+{row.pts} pts</span>
            </div>
          ))}
        </div>
      </div>

      {/* Admin Action — uniquement si statut "en_attente" */}
      {report.statut === "en_attente" && (
        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 18 }}>
          <div style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: ".08em", marginBottom: 12 }}>
            METTRE À JOUR LE STATUT
          </div>

          {/* CORRECTION : cvssScoreFinal envoyé dans le payload */}
          <Field label="Score CVSS final (0 – 10)">
            <input
              type="number" min="0" max="10" step="0.1"
              placeholder="ex: 8.5"
              value={cvss}
              onChange={e => setCvss(e.target.value)}
            />
          </Field>

          {/* CORRECTION : originalReportId requis pour duplicate */}
          <Field label="ID rapport original (si duplicate)">
            <input
              placeholder="uuid du rapport original"
              value={duplicateOf}
              onChange={e => setDuplicateOf(e.target.value)}
            />
          </Field>

          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn btn-success"
              style={{ flex: 1, justifyContent: "center" }}
              onClick={() => handleUpdate("validé")}
              disabled={loading}
            >
              <CheckCircle size={14} /> Valider
            </button>
            <button
              className="btn btn-danger"
              style={{ flex: 1, justifyContent: "center" }}
              onClick={() => handleUpdate("rejeté")}
              disabled={loading}
            >
              <XCircle size={14} /> Rejeter
            </button>
            <button
              className="btn btn-warning"
              style={{ flex: 1, justifyContent: "center" }}
              onClick={() => handleUpdate("duplicate")}
              disabled={loading || !duplicateOf}
              title={!duplicateOf ? "Renseignez l'ID du rapport original" : ""}
            >
              <AlertTriangle size={14} /> Duplicate
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

// CORRECTION : utilise ENDPOINTS.reportsByCompany → GET /reports/by-company (Auth + Entreprise / Admin)
const ReportsPage = ({ token }) => {
  const { data: reports, loading, refetch } = useFetch(ENDPOINTS.reportsByCompany, token);
  const { toasts, show, remove }            = useToast();
  const modal                               = useModal();
  const [search, setSearch]                 = useState("");
  const [statusFilter, setStatusFilter]     = useState("all");
  const [sevFilter, setSevFilter]           = useState("all");
  const [statusLoading, setStatusLoading]   = useState(false);

  /*
    CORRECTION : payload complet selon la doc
    PUT /reports/status → { reportId, status, cvssScoreFinal, originalReportId? }
  */
  const updateStatus = async ({ reportId, status, cvssScoreFinal, originalReportId }) => {
    setStatusLoading(true);
    try {
      const body = { reportId, status, cvssScoreFinal };
      if (originalReportId) body.originalReportId = originalReportId;

      await api(ENDPOINTS.reportStatus, {
        method: "PUT",
        body: JSON.stringify(body),
      }, token);

      show(
        status === "validé"    ? "✅ Rapport validé !"         :
        status === "rejeté"    ? "❌ Rapport rejeté."          :
                                 "⚠️ Rapport marqué duplicate."
      );
      modal.close();
      refetch();
    } catch (e) {
      show(e.message || "Erreur lors de la mise à jour", "error");
    } finally {
      setStatusLoading(false);
    }
  };

  const filtered = (reports || []).filter(r => {
    if (search && !r.titre?.toLowerCase().includes(search.toLowerCase()) && !r.id?.includes(search)) return false;
    if (statusFilter !== "all" && r.statut !== statusFilter) return false;
    if (sevFilter !== "all" && r.severite !== sevFilter) return false;
    return true;
  });

  const counts = {
    all:         (reports || []).length,
    en_attente:  (reports || []).filter(r => r.statut === "en_attente").length,
    "validé":    (reports || []).filter(r => r.statut === "validé").length,
    "rejeté":    (reports || []).filter(r => r.statut === "rejeté").length,
    "duplicate": (reports || []).filter(r => r.statut === "duplicate").length,
  };

  return (
    <div className="fade-up" style={{ padding: "24px 28px", overflowY: "auto", flex: 1 }}>
      <SectionHeader
        title="Rapports de Vulnérabilités"
        sub={`${counts.all} rapport(s) · ${counts.en_attente} en attente de traitement`}
        action={
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Rechercher un rapport..."
            style={{ width: 260 }}
          />
        }
      />

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 22 }}>
        {[
          { l: "Total",       v: counts.all,           c: T.blue   },
          { l: "En attente",  v: counts.en_attente,    c: T.yellow },
          { l: "Validés",     v: counts["validé"],     c: T.green  },
          { l: "Rejetés",     v: counts["rejeté"],     c: T.red    },
        ].map((s, i) => (
          <Card key={i} style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: T.muted }}>{s.l}</span>
            <span style={{ fontFamily: T.display, fontWeight: 900, fontSize: 22, color: s.c }}>{s.v}</span>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <Tabs
          tabs={[
            { id: "all",        label: "Tous",       count: counts.all },
            { id: "en_attente", label: "En attente", count: counts.en_attente },
            { id: "validé",     label: "Validés",    count: counts["validé"] },
            { id: "rejeté",     label: "Rejetés" },
            { id: "duplicate",  label: "Duplicates" },
          ]}
          active={statusFilter}
          onChange={setStatusFilter}
        />
        <Tabs
          tabs={[
            { id: "all",      label: "Toutes sévérités" },
            { id: "critique", label: "Critique" },
            { id: "haute",    label: "Haute" },
            { id: "moyenne",  label: "Moyenne" },
            { id: "faible",   label: "Faible" },
            { id: "info",     label: "Info" },
          ]}
          active={sevFilter}
          onChange={setSevFilter}
        />
      </div>

      {/* Table */}
      {loading ? <Spinner /> : filtered.length === 0 ? (
        <EmptyState icon={Bug} title="Aucun rapport trouvé" sub="Modifiez les filtres de recherche" />
      ) : (
        <Card>
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>TITRE</th>
                <th>HACKER</th>
                <th>PROGRAMME</th>
                <th>SÉVÉRITÉ</th>
                <th>STATUT</th>
                <th>CVSS</th>
                <th>DATE</th>
                <th>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr
                  key={r.id || i}
                  className="hov-row"
                  style={{ cursor: "pointer" }}
                  onClick={() => modal.openWith(r)}
                >
                  <td style={{ color: T.blue, fontWeight: 700, fontSize: 11, fontFamily: T.mono }}>
                    {r.id?.slice(0, 8) || `#${i + 1}`}
                  </td>
                  <td style={{ maxWidth: 200 }}>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12 }}>
                      {r.titre || "—"}
                    </div>
                  </td>
                  <td style={{ color: T.textSub, fontSize: 12 }}>
                    {r.hacker?.pseudo || r.hackerId?.slice(0, 8) || "—"}
                  </td>
                  <td style={{ color: T.textSub, fontSize: 11 }}>
                    {r.programme?.titre || r.programId?.slice(0, 8) || "—"}
                  </td>
                  <td><SeverityBadge severity={r.severite || cvssToSeverity(r.cvssScore)} /></td>
                  <td><StatusBadge status={r.statut || "en_attente"} /></td>
                  <td><CvssScore score={r.cvssScore || r.cvssScoreFinal} /></td>
                  <td style={{ color: T.muted, fontSize: 11 }}>{formatDate(r.createdAt)}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <button className="btn btn-ghost btn-sm btn-icon" onClick={() => modal.openWith(r)}>
                      <Eye size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <ReportDetailModal
        report={modal.data}
        open={modal.open}
        onClose={modal.close}
        onUpdateStatus={updateStatus}
        token={token}
        loading={statusLoading}
      />

      <ToastStack toasts={toasts} remove={remove} />
    </div>
  );
};

export default ReportsPage;